import { useEffect, useRef, useState } from 'react'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { AUTH_FAILED_EVENT, BEARER_STORAGE_KEY, apiBaseUrl } from '@/api/client'

export type SSEState = 'connecting' | 'open' | 'reconnecting' | 'closed'

export type SSEHandlers = {
  onConnect?: () => void
  onEvent: (eventName: string, data: unknown) => void
}

type Subscription = {
  id: number
  onEvent: (eventName: string, data: unknown) => void
  onConnect: (() => void) | undefined
  onState: (state: SSEState) => void
}

type Connection = {
  topicsKey: string
  topics: string[]
  state: SSEState
  subs: Map<number, Subscription>
  abort: AbortController
  reconnectTimer: number | null
  attempt: number
  visibilityHandler: (() => void) | null
  authFailedHandler: (() => void) | null
}

const MAX_BACKOFF_MS = 30_000

function backoffDelay(attempt: number): number {
  return Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt)
}

class SSEManager {
  private connections = new Map<string, Connection>()
  private nextId = 1

  subscribe(
    topics: string[],
    handlers: {
      onEvent: (eventName: string, data: unknown) => void
      onConnect?: () => void
      onState: (state: SSEState) => void
    },
  ): () => void {
    if (topics.length === 0) {
      handlers.onState('closed')
      return () => undefined
    }
    const sortedTopics = [...topics].sort()
    const topicsKey = sortedTopics.join(',')
    const id = this.nextId++

    let conn = this.connections.get(topicsKey)
    if (!conn) {
      conn = this.createConnection(topicsKey, sortedTopics)
      this.connections.set(topicsKey, conn)
      this.openConnection(conn)
    }
    const sub: Subscription = {
      id,
      onEvent: handlers.onEvent,
      onConnect: handlers.onConnect,
      onState: handlers.onState,
    }
    conn.subs.set(id, sub)
    sub.onState(conn.state)
    if (conn.state === 'open' && handlers.onConnect) handlers.onConnect()

    return () => this.unsubscribe(topicsKey, id)
  }

  private createConnection(topicsKey: string, topics: string[]): Connection {
    return {
      topicsKey,
      topics,
      state: 'connecting',
      subs: new Map(),
      abort: new AbortController(),
      reconnectTimer: null,
      attempt: 0,
      visibilityHandler: null,
      authFailedHandler: null,
    }
  }

  private setState(conn: Connection, state: SSEState): void {
    if (conn.state === state) return
    conn.state = state
    for (const sub of conn.subs.values()) sub.onState(state)
  }

  private dispatchConnect(conn: Connection): void {
    for (const sub of conn.subs.values()) sub.onConnect?.()
  }

  private dispatchEvent(conn: Connection, eventName: string, data: unknown): void {
    for (const sub of conn.subs.values()) sub.onEvent(eventName, data)
  }

  private buildUrl(topics: string[]): string | null {
    const token =
      typeof window === 'undefined'
        ? null
        : window.localStorage.getItem(BEARER_STORAGE_KEY)
    if (!token) return null
    const params = new URLSearchParams({
      topics: topics.join(','),
      token,
    })
    return `${apiBaseUrl}/events?${params.toString()}`
  }

  private openConnection(conn: Connection): void {
    if (conn.reconnectTimer != null) {
      window.clearTimeout(conn.reconnectTimer)
      conn.reconnectTimer = null
    }

    const url = this.buildUrl(conn.topics)
    if (!url) {
      this.setState(conn, 'closed')
      this.armAuthFailedRetry(conn)
      return
    }

    conn.abort = new AbortController()
    this.setState(conn, conn.attempt > 0 ? 'reconnecting' : 'connecting')
    this.armAuthFailedRetry(conn)

    void fetchEventSource(url, {
      signal: conn.abort.signal,
      // openWhenHidden:true keeps the connection alive in background tabs;
      // we drive our own visibility-aware reconnect logic in onerror.
      openWhenHidden: true,
      onopen: async (response) => {
        if (
          response.ok &&
          response.headers
            .get('content-type')
            ?.toLowerCase()
            .includes('text/event-stream')
        ) {
          conn.attempt = 0
          return
        }
        if (response.status === 401) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(AUTH_FAILED_EVENT))
          }
          throw new SSEFatalError('auth-failed')
        }
        throw new Error(
          `SSE handshake failed (${response.status} ${response.statusText})`,
        )
      },
      onmessage: (ev) => {
        if (ev.event === 'hello') {
          this.setState(conn, 'open')
          this.dispatchConnect(conn)
          return
        }
        if (!ev.event && !ev.data) return
        let data: unknown = null
        if (ev.data) {
          try {
            data = JSON.parse(ev.data)
          } catch {
            data = ev.data
          }
        }
        this.dispatchEvent(conn, ev.event || 'message', data)
      },
      onclose: () => {
        if (conn.abort.signal.aborted) return
        throw new SSEClosedError()
      },
      onerror: (err) => {
        if (err instanceof SSEFatalError) {
          this.setState(conn, 'closed')
          throw err
        }
        if (conn.abort.signal.aborted) throw new SSEAbortedError()
        throw err
      },
    }).catch((err) => {
      if (err instanceof SSEFatalError) {
        this.setState(conn, 'closed')
        return
      }
      if (err instanceof SSEAbortedError) {
        this.setState(conn, 'closed')
        return
      }
      if (conn.abort.signal.aborted) {
        this.setState(conn, 'closed')
        return
      }
      this.scheduleReconnect(conn)
    })
  }

  private scheduleReconnect(conn: Connection): void {
    this.setState(conn, 'reconnecting')
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this.armVisibilityResume(conn)
      return
    }
    const delay = backoffDelay(conn.attempt)
    conn.attempt += 1
    conn.reconnectTimer = window.setTimeout(() => {
      conn.reconnectTimer = null
      this.openConnection(conn)
    }, delay)
  }

  private armVisibilityResume(conn: Connection): void {
    if (conn.visibilityHandler) return
    const handler = () => {
      if (document.visibilityState !== 'visible') return
      document.removeEventListener('visibilitychange', handler)
      conn.visibilityHandler = null
      this.openConnection(conn)
    }
    conn.visibilityHandler = handler
    document.addEventListener('visibilitychange', handler)
  }

  private armAuthFailedRetry(conn: Connection): void {
    if (conn.authFailedHandler) return
    const handler = () => {
      conn.attempt = 0
      this.openConnection(conn)
    }
    conn.authFailedHandler = handler
    if (typeof window !== 'undefined') {
      window.addEventListener(AUTH_FAILED_EVENT, handler)
    }
  }

  private disposeConnection(conn: Connection): void {
    if (conn.reconnectTimer != null) {
      window.clearTimeout(conn.reconnectTimer)
      conn.reconnectTimer = null
    }
    if (conn.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', conn.visibilityHandler)
      conn.visibilityHandler = null
    }
    if (conn.authFailedHandler && typeof window !== 'undefined') {
      window.removeEventListener(AUTH_FAILED_EVENT, conn.authFailedHandler)
      conn.authFailedHandler = null
    }
    conn.abort.abort()
    this.connections.delete(conn.topicsKey)
  }

  private unsubscribe(topicsKey: string, id: number): void {
    const conn = this.connections.get(topicsKey)
    if (!conn) return
    conn.subs.delete(id)
    if (conn.subs.size === 0) {
      this.disposeConnection(conn)
    }
  }
}

class SSEFatalError extends Error {}
class SSEClosedError extends Error {}
class SSEAbortedError extends Error {}

const manager = new SSEManager()

export function useSSE(
  topics: string[],
  handlers: SSEHandlers,
): { state: SSEState } {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  const [state, setState] = useState<SSEState>('connecting')
  const topicsKey = topics.join('|')

  useEffect(() => {
    const list = topicsKey ? topicsKey.split('|') : []
    if (list.length === 0) {
      setState('closed')
      return
    }
    const unsubscribe = manager.subscribe(list, {
      onEvent: (name, data) => handlersRef.current.onEvent(name, data),
      onConnect: () => handlersRef.current.onConnect?.(),
      onState: setState,
    })
    return () => unsubscribe()
  }, [topicsKey])

  return { state }
}

export function combineLiveStates(
  ...states: ReadonlyArray<SSEState | undefined>
): SSEState {
  let hasOpen = false
  let hasReconnecting = false
  let hasConnecting = false
  for (const s of states) {
    if (s === 'open') hasOpen = true
    else if (s === 'reconnecting') hasReconnecting = true
    else if (s === 'connecting') hasConnecting = true
  }
  if (hasOpen) return 'open'
  if (hasReconnecting) return 'reconnecting'
  if (hasConnecting) return 'connecting'
  return 'closed'
}
